import React, { useCallback } from 'react';

import {
  overrideMapControlledInputValue,
  standardWeightPlaceholderString,
} from './rubricWeightSchemeUtils';
import RubricQuestionSetWeightRow from './RubricQuestionSetWeightRow';
import RubricQuestionWeightRow from './RubricQuestionWeightRow';

import styles from './RubricWeights.module.scss';

/**
 * Edits a single guid → weight override map against a rubric document's question sets.
 * Empty fields inherit standard weights (same semantics as rubric weight_overrides).
 */
function RubricWeightSchemeEditor({
  rubricDocument,
  draftWeights = {},
  committedWeights = {},
  onWeightChange = () => {},
}) {
  const questionSets = rubricDocument?.question_sets ?? [];

  const handleWeightChange = useCallback(
    (nodeGuid, rawValue) => {
      if (!nodeGuid) return;
      onWeightChange(nodeGuid, rawValue);
    },
    [onWeightChange]
  );

  if (questionSets.length === 0) {
    return (
      <p className="text-muted mb-0">
        This rubric has no question sets to weight.
      </p>
    );
  }

  return (
    <div className={styles.weights}>
      <div className={styles.grid}>
        <div className={styles['grid-header']}>
          <div className={styles['header-cell']} />
          <div className={styles['header-cell']}>Weight</div>
        </div>

        {questionSets.map((qs) => (
          <div className="row-stripe-reset" key={qs.guid}>
            <RubricQuestionSetWeightRow
              questionSet={qs}
              isStandardView={false}
              draftWeight={overrideMapControlledInputValue(
                draftWeights,
                qs.guid
              )}
              standardWeight={standardWeightPlaceholderString(qs.weight)}
              committedWeight={overrideMapControlledInputValue(
                committedWeights,
                qs.guid
              )}
              onChange={handleWeightChange}
            />
            {(qs.questions ?? []).map((q) => (
              <RubricQuestionWeightRow
                key={q.guid}
                questionSet={qs}
                question={q}
                isStandardView={false}
                draftWeight={overrideMapControlledInputValue(
                  draftWeights,
                  q.guid
                )}
                standardWeight={standardWeightPlaceholderString(q.weight)}
                committedWeight={overrideMapControlledInputValue(
                  committedWeights,
                  q.guid
                )}
                onChange={handleWeightChange}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default RubricWeightSchemeEditor;
